import { motion } from "framer-motion";
import { Check, MessageCircle, Truck, ShieldCheck } from "lucide-react";
import { MaskedLine } from "../landing/shared";
import { SITE, IMAGES, waLink } from "@/config";

const Hero = () => (
  <section data-testid="hero-section" className="bg-ivory pt-20 sm:pt-24">
    <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 px-4 pb-14 sm:px-6 lg:grid lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pb-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 18 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full lg:order-2"
      >
        <div className="relative overflow-hidden rounded-[1.75rem] shadow-[0_30px_60px_-25px_rgba(27,42,74,0.45)]">
          <img
            src={IMAGES.hero}
            alt="Medlinkx Premium Toilet Footstool in use"
            data-testid="hero-product-image"
            className="aspect-square w-full object-cover"
          />
        </div>
      </motion.div>

      <div className="w-full lg:order-1">
        <h1 data-testid="hero-headline">
          <MaskedLine delay={0.15}>
            <span className="font-logo text-[11vw] font-extrabold leading-none tracking-[0.02em] sm:text-6xl lg:text-7xl">
              <span className="text-navy">MEDLINK</span>
              <span className="text-gold">X</span>
            </span>
          </MaskedLine>
          <MaskedLine delay={0.28}>
            <span className="mt-2 block font-sans text-[6vw] font-extrabold uppercase leading-tight tracking-[0.08em] text-navy sm:text-3xl lg:text-4xl">
              Constipation Care Kit
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-4 font-serif text-2xl italic text-navy/75 sm:text-3xl"
          data-testid="hero-tagline"
        >
          &ldquo;A Better Way to Go.&rdquo;
        </motion.p>

        <motion.ul
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.58 }}
          className="mt-6 space-y-2.5"
          data-testid="hero-includes-list"
        >
          <li className="flex items-center gap-3 text-base font-bold text-navy sm:text-lg" data-testid="hero-include-footstool">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy">
              <Check className="h-3.5 w-3.5 text-gold" strokeWidth={3} />
            </span>
            Premium Toilet Footstool
          </li>
          <li className="flex items-center gap-3 text-base font-bold text-navy sm:text-lg" data-testid="hero-include-counselling">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15">
              <Check className="h-3.5 w-3.5 text-gold" strokeWidth={3} />
            </span>
            <span>
              <span className="text-gold">+ FREE</span> Doctor Counselling
            </span>
          </li>
          <li className="flex items-center gap-3 text-base font-bold text-navy sm:text-lg" data-testid="hero-include-guide">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15">
              <Check className="h-3.5 w-3.5 text-gold" strokeWidth={3} />
            </span>
            <span>
              <span className="text-gold">+ FREE</span> Constipation Guide
            </span>
          </li>
        </motion.ul>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 flex items-baseline gap-3"
        >
          <span className="font-serif text-5xl font-semibold text-navy" data-testid="hero-price">
            {SITE.price}
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-charcoal/50">
            Complete kit
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <a
            data-testid="hero-buy-now-button"
            href={SITE.razorpayUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-gold px-9 py-5 text-base font-extrabold uppercase tracking-[0.12em] text-navy shadow-[0_18px_40px_-12px_rgba(185,155,92,0.65)] transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-soft hover:shadow-[0_24px_48px_-14px_rgba(185,155,92,0.7)] active:translate-y-0 sm:py-4 sm:text-sm"
          >
            Buy Now — {SITE.price}
          </a>
          <a
            data-testid="hero-whatsapp-button"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-wa/70 px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-wa-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-wa hover:text-[#063A2C]"
          >
            <MessageCircle className="h-4 w-4" />
            Order on WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2.5"
          data-testid="hero-trust-strip"
        >
          <span className="flex items-center gap-2 text-xs font-semibold text-charcoal/60">
            <Truck className="h-4 w-4 text-gold" />
            Free shipping across India
          </span>
          <span className="flex items-center gap-2 text-xs font-semibold text-charcoal/60">
            <ShieldCheck className="h-4 w-4 text-gold" />
            Secure payment via Razorpay — UPI & Cards
          </span>
        </motion.div>
      </div>
    </div>
  </section>
);

export default Hero;
